/**
 * The generation SEQUENCER (feature 0059 lot 1d) — pure orchestration, no store, no network of its own.
 *
 * Runs the 0059 §5 construction scene over the theme workspace: pages in the order
 * PRODUCT → HOME → COLLECTION (§5.1 — the product page is where the AI proves it read the seller's
 * catalog, and the home consumes what the product established; collection is nearly free), and within
 * a page, sections in DOCUMENT order (the scene reveals top to bottom).
 *
 * THE SAFETY-NET RULE IS THE CONTROL FLOW (§5.3): a section that fails keeps the theme's copy and the
 * run CONTINUES — the page is presentable at every instant. Only two things end a run early: the
 * orchestrator being unconfigured (503 — every further call would 503 too), and an abort from the
 * seller. Every section, written or not, is emitted with its reason — the "à toi de remplir" list for
 * integrity skips is what separates our output from a page still saying "About Us Information".
 */
import {
  getRecipe,
  themeIdFor,
  type CategoryRecipe,
  type PagePlan,
  type SectionPlan,
  type TemplateKey,
  type TextPatch,
} from '@aquilixshop/ui-react';
import { AiUnavailableError } from '../../lib/aiApi';
import type { AiGenerateImageRequest, AiGenerateRequest } from '../../lib/aiApi';
import { toSlotDto, valuesToPatches } from './slotKeys';
import { bt, btp } from '../../i18n';

/**
 * §5.1 order, minus collection (founder 2026-08-20: «retirer collection, ça n'a aucun sens») — a
 * collection page is a filtered grid, its copy is two labels; generating it read as noise. `label` is
 * what the scene shows while the page is under way.
 */
export const GENERATION_PAGES: ReadonlyArray<{ template: TemplateKey; label: string }> = [
  { template: 'product', label: bt('chrome.gen.page_product', 'The product page') },
  { template: 'index', label: bt('chrome.gen.page_home', 'The home page') },
];

/**
 * THE FROM-SCRATCH COMPOSITION (founder 2026-08-21: «jamais choisir les composants en se basant sur
 * une catégorie ou template — partir de 0 et construire parmi l'existant»). One commerce-first
 * blueprint for EVERY store: sections picked from the component library and instantiated blank
 * (schema presets, no theme content), in selling order. The category no longer decides structure —
 * it only skins colors and typography. Every type below carries a UNIVERSAL_ROLE (texts) and its
 * images are generated by the image phase; testimonials/results stay out (never invented).
 */
export const SCRATCH_BLUEPRINT: Readonly<Partial<Record<TemplateKey, readonly string[]>>> = {
  product: [
    'main-product',
    'trust-row',
    'image-with-text',
    'custom-columns',
    'collapsible-content',
    'faq',
    'related-products',
  ],
  index: [
    'slideshow-hero',
    'icon-bar',
    'featured-product',
    'image-with-text',
    'custom-columns',
    'faq',
    'newsletter',
  ],
};

/**
 * Section types whose lead image is worth generating from the prompt (0059 images): the openers — the
 * image that makes a page read as THIS store's page. One per page, never a gallery: cost discipline.
 */
export const HERO_IMAGE_TYPES: readonly string[] = [
  'slideshow-hero',
  'hero',
  'parallax-hero',
  'slideshow',
  'image-with-text',
];

/** One image field at one address — the deps resolve it from `collectImageSlots` on the open page. */
export interface ImageTarget {
  sectionId: string;
  /** Set when the field lives on a block (a multicolumn card, a slide) — deep ids resolve too. */
  blockId?: string;
  fieldId: string;
  sectionType: string;
  /** Short prose already on the node (the card's own title) — steers THIS image, not the page's. */
  context: string;
  /** The field currently holds a (theme) image. The run replaces what SHOWS — empties stay empty. */
  hadImage: boolean;
  /** False = template pixels to STRIP, never a slot to generate into (logos, badges, avatars…). */
  generatable: boolean;
}

/**
 * What an image slot shows WHILE its image generates (founder 2026-08-21: «une animation à la place
 * de l'image le temps que ça se génère pour que le seller le sache») — a pulsing sparkle on a soft
 * wash, self-contained as a data URI so it renders inside any section's plain `<img>` with zero
 * component changes. Semi-transparent grays: legible on light AND dark schemes. It exists ONLY during
 * the run: every marked slot ends as a generated image or is cleaned to empty (never published).
 */
export const GENERATING_MEDIA_URI: string =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 800">' +
      '<rect width="1200" height="800" fill="#8b90a0" opacity="0.14"/>' +
      '<g transform="translate(600 400)">' +
      '<path d="M0 -70 L16 -16 L70 0 L16 16 L0 70 L-16 16 L-70 0 L-16 -16 Z" fill="#9aa0b4">' +
      '<animate attributeName="opacity" values="0.35;0.85;0.35" dur="1.6s" repeatCount="indefinite"/>' +
      '<animateTransform attributeName="transform" type="scale" values="0.85;1.08;0.85" dur="1.6s" repeatCount="indefinite"/>' +
      '</path>' +
      '<path d="M104 -104 L112 -80 L136 -72 L112 -64 L104 -40 L96 -64 L72 -72 L96 -80 Z" fill="#9aa0b4">' +
      '<animate attributeName="opacity" values="0.2;0.65;0.2" dur="1.6s" begin="0.5s" repeatCount="indefinite"/>' +
      '</path>' +
      '</g></svg>',
  );

/** Stable identity of a slot across the two listings (theme contexts, then generated contexts). */
function targetKey(t: ImageTarget): string {
  return `${t.sectionId}/${t.blockId ?? ''}/${t.fieldId}`;
}

/**
 * The slots this run generates: every generatable slot, capped. The page is OUR from-scratch
 * composition — its image slots exist because the blueprint chose sections that want images, so
 * empty-vs-filled no longer matters (that distinction belonged to the theme-arrangement era).
 * Non-generatable slots (logos, badges, avatars) stay out, stripped by the virgin start.
 */
export function pickImageTargets(slots: ImageTarget[]): ImageTarget[] {
  return slots.filter((t) => t.generatable).slice(0, MAX_IMAGES_PER_PAGE);
}

/**
 * Cost/latency bound per page, not a coverage promise: past the cap the theme's images stand. A real
 * page carries 3-8 writable image fields; the cap only bites on pathological documents.
 */
export const MAX_IMAGES_PER_PAGE = 8;
/** Provider calls in flight at once — image generation is tens of seconds, serial would take minutes. */
const IMAGE_CONCURRENCY = 3;
/**
 * Consecutive failed TEXT sections before the run declares the provider down and stops. Dead credits
 * answer 400 (not 503), so without this breaker the 2026-08-21 run «wrote» a whole store where every
 * section kept the theme's demo copy, then burnt image credits decorating it.
 */
const TEXT_FAILURE_BREAKER = 4;

/** What the engine needs from the outside world — the store and the API, behind these functions. */
export interface EngineDeps {
  /** Navigate the builder to the workspace draft of `template`. False = the theme has no such page. */
  openPage(template: TemplateKey): Promise<boolean>;
  /**
   * REBUILD the open page from scratch: drop every template-zone section, insert `types` in order
   * (schema presets, store language), then ground catalog pickers on the fresh sections.
   */
  composePage(types: readonly string[]): void;
  /** The plan of the CURRENTLY open page (planPage over the composed document). */
  planCurrentPage(recipe: CategoryRecipe): PagePlan | null;
  /** One backend call — throws AiUnavailableError (run-fatal) or AiGenerationError (section-local). */
  generate(request: AiGenerateRequest): Promise<Record<string, string>>;
  /** Write values through the store's normal edit path. Returns what actually landed. */
  applyPatches(patches: TextPatch[]): { applied: number; rejected: number };
  /** Select + scroll the canvas to the section — the liseré of §5.2 via the existing selection frame. */
  reveal(sectionId: string): void;
  /**
   * EVERY AI-writable image slot on the CURRENT page, render order (founder 2026-08-20: «tout le
   * contenu des composants est géré à 100 % par l'IA» — the whole surface, not just the hero).
   */
  listImageTargets(): ImageTarget[];
  /**
   * One decor image — throws AiUnavailableError (turns images off for the run) or AiGenerationError.
   * `onPartial` (when the provider streams) receives data-URI frames to paint while it materializes.
   */
  generateImage(
    request: AiGenerateImageRequest,
    onPartial?: (dataUri: string) => void,
  ): Promise<string>;
  /** Write the image through the store's normal edit path (section- or block-level by the address). */
  applyImage(target: ImageTarget, url: string, alt: string): void;
  /**
   * Empty the field so the section renders its neutral PlaceholderMedia (founder 2026-08-21: «il ne
   * faut récupérer AUCUNE image d'aucune template» — the demo photo must never be on screen, not even
   * as a stand-in while its replacement generates).
   */
  clearImage(target: ImageTarget): void;
}

export interface GenerationInput {
  categoryId: string;
  storeName: string;
  /** The seller's one phrase — may be empty; the recipes still carry the voice. */
  storeDescription: string;
  /** fr | ar | en — the language the AI WRITES in (§9: never write-then-translate). */
  locale: string;
  /** The store's REAL products («Produits réellement vendus : …») — grounds every section's copy. */
  productContext?: string;
  /**
   * The CHOSEN product's cover (founder 2026-08-20: the flow starts from a product). When AI images
   * are unavailable or fail, the hero shows THIS — the seller's real product — never the theme's demo
   * photo (the «boutique de lits» fix, applied to pixels).
   */
  heroImageFallback?: { url: string; alt: string };
  /**
   * The marketing-expert composer's per-page section lists (founder 2026-08-21) — already sanitized
   * against the menu and the registry. Absent or null per page → SCRATCH_BLUEPRINT fallback.
   */
  composition?: Partial<Record<TemplateKey, readonly string[] | null>>;
}

export type GenerationEvent =
  | { type: 'page-start'; template: TemplateKey; label: string; sectionsToWrite: number }
  | { type: 'page-absent'; template: TemplateKey }
  | { type: 'section-start'; sectionId: string; activity: string }
  | { type: 'section-done'; sectionId: string; applied: number; rejected: number }
  | {
      type: 'section-skipped';
      sectionId: string;
      sectionType: string;
      reason: 'integrity' | 'keep';
    }
  | { type: 'section-failed'; sectionId: string; message: string }
  | { type: 'image-start'; sectionId: string }
  | { type: 'image-done'; sectionId: string }
  | { type: 'image-skipped'; reason: 'unavailable' | 'failed' | 'no-target' }
  | { type: 'page-done'; template: TemplateKey };

export interface GenerationSummary {
  status: 'done' | 'unavailable' | 'aborted';
  sectionsWritten: number;
  sectionsFailed: number;
  slotsApplied: number;
  imagesGenerated: number;
  /** True when the image provider answered 503 — the seller learns images need a key, not that they failed. */
  imagesUnavailable: boolean;
  /** Section TYPES the integrity rule left to the seller ("à toi de remplir"). */
  integritySkips: string[];
}

/**
 * What the scene's étiquette says while a section is being written — "Rédige la FAQ", never a spinner
 * with "en cours" (§5.2 point 3: the label naming the work is what separates INTELLIGENT from SLOW).
 */
export function activityLabel(plan: SectionPlan): string {
  // The seller reads this étiquette in HER language — the same dictionary as the rest of the chrome.
  const hook = bt('chrome.gen.act.hook', 'Choosing the hook');
  const byType: Record<string, string> = {
    faq: bt('chrome.gen.act.faq', 'Writing the FAQ'),
    'slideshow-hero': hook,
    hero: hook,
    'parallax-hero': hook,
    slideshow: hook,
    'main-product': bt('chrome.gen.act.main_product', 'Writing the product sheet'),
    'collapsible-content': bt('chrome.gen.act.objections', 'Answering objections'),
    'image-with-text': bt('chrome.gen.act.brand_story', 'Telling the brand story'),
    'trust-row': bt('chrome.gen.act.guarantees', 'Wording the guarantees'),
    'icon-bar': bt('chrome.gen.act.reasons', 'Wording the reasons to buy'),
    newsletter: bt('chrome.gen.act.newsletter', 'Writing the sign-up invitation'),
    'collection-list': bt('chrome.gen.act.collections', 'Naming the collections'),
    'featured-collection': bt('chrome.gen.act.featured', 'Presenting the selection'),
    'anchor-nav': bt('chrome.gen.act.anchors', 'Laying out the page anchors'),
  };
  const name = plan.slots[0]?.nodeName ?? plan.type;
  return byType[plan.type] ?? btp('chrome.gen.act.writing_named', 'Writing “{name}”', { name });
}

export async function runGeneration(
  input: GenerationInput,
  deps: EngineDeps,
  emit: (event: GenerationEvent) => void,
  isAborted: () => boolean = () => false,
): Promise<GenerationSummary> {
  const recipe = getRecipe(input.categoryId);
  let consecutiveTextFailures = 0;
  const summary: GenerationSummary = {
    status: 'done',
    sectionsWritten: 0,
    sectionsFailed: 0,
    slotsApplied: 0,
    imagesGenerated: 0,
    imagesUnavailable: false,
    integritySkips: [],
  };

  for (const page of GENERATION_PAGES) {
    if (isAborted()) return { ...summary, status: 'aborted' };
    const open = await deps.openPage(page.template);
    if (!open) {
      emit({ type: 'page-absent', template: page.template });
      continue;
    }
    // FROM SCRATCH: the theme's arrangement is discarded — the page is rebuilt before anything is
    // planned, revealed or written. The COMPOSER's marketing pick wins; the fixed blueprint is the
    // fallback when the composer failed or answered garbage.
    const blueprint = input.composition?.[page.template] ?? SCRATCH_BLUEPRINT[page.template];
    if (blueprint) deps.composePage(blueprint);
    const plan = deps.planCurrentPage(recipe);
    if (!plan) {
      emit({ type: 'page-absent', template: page.template });
      continue;
    }
    emit({
      type: 'page-start',
      template: page.template,
      label: page.label,
      sectionsToWrite: plan.sections.filter((s) => s.policy === 'write').length,
    });

    // VIRGIN START (founder 2026-08-21: «partir d'une configuration vierge où il n'y a aucune
    // photo») : before the first section is revealed the page is stripped BARE — every template
    // pixel goes, wherever it lives (decor, avatars, logos, badges, beyond-cap slots). The slots the
    // run will fill wear the GENERATING animation; everything else is simply emptied. Only the
    // seller's real product imagery (main-product/buy box, never listed) survives.
    const marked = new Set<string>();
    {
      const all = deps.listImageTargets();
      const generating = new Set(
        summary.imagesUnavailable ? [] : pickImageTargets(all).map(targetKey),
      );
      for (const slot of all) {
        if (generating.has(targetKey(slot))) {
          deps.applyImage(slot, GENERATING_MEDIA_URI, '');
          marked.add(targetKey(slot));
        } else if (slot.hadImage) {
          deps.clearImage(slot);
        }
      }
    }
    // Runs on EVERY exit from this page — normal end, abort, provider breaker: a slot still wearing
    // the generating animation is emptied, because the animation is a promise, never content.
    const cleanupMarked = (): void => {
      for (const target of deps.listImageTargets()) {
        if (marked.has(targetKey(target))) deps.clearImage(target);
      }
    };

    for (const section of plan.sections) {
      if (isAborted()) {
        cleanupMarked();
        return { ...summary, status: 'aborted' };
      }
      if (section.policy !== 'write') {
        // Integrity is REPORTED, never swallowed — the seller must learn which parts are his to fill,
        // instead of discovering demo reviews on his live store.
        if (section.skipped === 'integrity') {
          summary.integritySkips.push(section.type);
          emit({
            type: 'section-skipped',
            sectionId: section.sectionId,
            sectionType: section.type,
            reason: 'integrity',
          });
        } else {
          emit({
            type: 'section-skipped',
            sectionId: section.sectionId,
            sectionType: section.type,
            reason: 'keep',
          });
        }
        continue;
      }

      deps.reveal(section.sectionId);
      emit({
        type: 'section-start',
        sectionId: section.sectionId,
        activity: activityLabel(section),
      });

      const slots = section.slots
        .map(toSlotDto)
        .filter((s): s is NonNullable<typeof s> => s !== null);
      if (slots.length === 0) {
        emit({
          type: 'section-skipped',
          sectionId: section.sectionId,
          sectionType: section.type,
          reason: 'keep',
        });
        continue;
      }
      try {
        const values = await deps.generate({
          capability: 'SECTION',
          locale: input.locale,
          voice: recipe.voice,
          role: section.role ?? '',
          storeName: input.storeName,
          storeCategory: recipe.label,
          storeDescription: input.storeDescription,
          productContext: input.productContext,
          slots,
        });
        const result = deps.applyPatches(valuesToPatches(values));
        summary.sectionsWritten += 1;
        consecutiveTextFailures = 0;
        summary.slotsApplied += result.applied;
        emit({
          type: 'section-done',
          sectionId: section.sectionId,
          applied: result.applied,
          rejected: result.rejected,
        });
      } catch (e) {
        if (e instanceof AiUnavailableError) {
          // 503 is env-wide: every further call would 503 too. Stop cleanly; the theme copy stands.
          // cleanupMarked FIRST (fixed 2026-08-23): this was the one exit from the page that skipped
          // it, so a 503 on the very first section left every image slot holding the GENERATING
          // sparkle — a data-URI placeholder the seller could then publish. The animation is a
          // promise, never content: no exit may leave it behind.
          cleanupMarked();
          return { ...summary, status: 'unavailable' };
        }
        summary.sectionsFailed += 1;
        consecutiveTextFailures += 1;
        emit({
          type: 'section-failed',
          sectionId: section.sectionId,
          message: e instanceof Error ? e.message : 'generation failed',
        });
        if (consecutiveTextFailures >= TEXT_FAILURE_BREAKER && summary.sectionsWritten === 0) {
          // Nothing has EVER succeeded and failures keep coming: the provider is down for this run
          // (dead credits, outage). Stop burning calls; the seller sees «indisponible», not a store
          // of theme copy that pretends to be generated.
          cleanupMarked();
          return { ...summary, status: 'unavailable' };
        }
      }
    }
    // ── The page's images (0059 — «100 % IA»): EVERY writable image slot, not just the hero, so no
    // theme demo photo survives a generation. A 503 turns images off for the WHOLE run (every further
    // call would 503 too) without touching the text flow; a single failed image keeps the theme's —
    // except the hero, which falls back to the seller's real product photo (§5.3 applied to pixels).
    if (!summary.imagesUnavailable && !isAborted()) {
      // Re-listed (not reused): the text phase just rewrote every card title, and `context` steers
      // each image — the fresh listing prompts with the SELLER's copy, not the theme's. Filtered to
      // the marked set so the two listings can never drift apart.
      const targets = deps.listImageTargets().filter((t) => marked.has(targetKey(t)));
      if (targets.length === 0) {
        emit({ type: 'image-skipped', reason: 'no-target' });
      } else {
        const heroIndex = targets.findIndex((t) => HERO_IMAGE_TYPES.includes(t.sectionType));
        const first = targets[0];
        if (first) deps.reveal(first.sectionId);
        let cursor = 0;
        const worker = async (): Promise<void> => {
          while (!summary.imagesUnavailable && !isAborted()) {
            const i = cursor++;
            const target = targets[i];
            if (!target) return;
            emit({ type: 'image-start', sectionId: target.sectionId });
            try {
              const alt = target.context || input.storeDescription.slice(0, 120);
              const url = await deps.generateImage(
                {
                  prompt: imagePrompt(input, recipe.label, page.template, target.context),
                  aspectRatio: i === heroIndex ? '16:9' : '1:1',
                },
                // STREAMING (founder 2026-08-21): each partial frame lands straight in the slot — the
                // seller watches the image materialize where the sparkle was.
                (dataUri) => deps.applyImage(target, dataUri, alt),
              );
              deps.applyImage(target, url, target.context || input.storeDescription.slice(0, 120));
              summary.imagesGenerated += 1;
              marked.delete(targetKey(target));
              emit({ type: 'image-done', sectionId: target.sectionId });
            } catch (e) {
              if (e instanceof AiUnavailableError) {
                summary.imagesUnavailable = true;
                emit({ type: 'image-skipped', reason: 'unavailable' });
              } else {
                emit({ type: 'image-skipped', reason: 'failed' });
              }
              // A failed OPENER falls back to the seller's real product photo; the cleanup below
              // empties any other failure — «aucune image de template», even on a bad day.
              if (i === heroIndex && input.heroImageFallback) {
                deps.applyImage(target, input.heroImageFallback.url, input.heroImageFallback.alt);
                marked.delete(targetKey(target));
                emit({ type: 'image-done', sectionId: target.sectionId });
              }
            }
          }
        };
        await Promise.all(
          Array.from({ length: Math.min(IMAGE_CONCURRENCY, targets.length) }, () => worker()),
        );
      }
    }
    cleanupMarked();

    emit({ type: 'page-done', template: page.template });
  }
  return summary;
}

/**
 * The image brief: the SELLER's phrase carries the subject; the guardrails are ours. "Sans texte" is
 * load-bearing — image models write garbled words into banners the moment a store name is mentioned,
 * and a hero with gibberish typography is worse than the theme's own photo.
 */
export function imagePrompt(
  input: GenerationInput,
  categoryLabel: string,
  template: TemplateKey,
  context = '',
): string {
  const scene =
    template === 'product'
      ? 'gros plan produit, mise en scène sobre'
      : 'ambiance de marque, plan large';
  const subject = context ? ` Sujet précis de cette image : ${context.replace(/\s+/g, ' ')}.` : '';
  return (
    `Photographie e-commerce professionnelle, ${scene}. Boutique : ${input.storeDescription || categoryLabel}. ` +
    `${input.productContext ? `${input.productContext.slice(0, 200)}. ` : ''}` +
    `Style ${categoryLabel}.${subject} Lumière naturelle, composition épurée. Sans texte, sans logo, sans lettrage.`
  );
}

/** The theme a category applies — re-exported so the store layer has one import site. */
export { themeIdFor };
